import { useState } from 'react';
import {
  AccountAssignment,
  ExistingAccountAssignment,
  MatchedAccountNeedingEmployees,
} from './NewAccountReviewModal';

interface NewAccount {
  account_string: string;
  transaction_count: number;
  total_commission: number;
}

export type ExistingAssignmentMap = Record<
  number,
  { user_assignments: { user_id: number; commission_percentage: number }[] }
>;

/**
 * Owns the two separate assignment maps for NewAccountReviewModal —
 * new accounts keyed by account_string, existing-empty accounts keyed by
 * account_id. Keeping them split avoids the key-collision class of bugs
 * the architect flagged when a brand-new account's matching code happens
 * to numerically equal an existing account_id.
 */
export function useReviewModalAssignments(
  newAccounts: NewAccount[],
  matchedAccountsNeedingEmployees: MatchedAccountNeedingEmployees[],
  // R15 Task B1: optional prior state carried across a 422 retry so the
  // user does not lose the assignments they just entered. When provided,
  // the reset seeds rows from these maps instead of starting empty.
  initialNew?: Record<string, AccountAssignment>,
  initialExisting?: ExistingAssignmentMap
) {
  const [newAssignments, setNewAssignments] = useState<Record<string, AccountAssignment>>({});
  const [existingAssignments, setExistingAssignments] = useState<ExistingAssignmentMap>({});

  const reset = () => {
    const initialNewMap: Record<string, AccountAssignment> = {};
    newAccounts.forEach((acc) => {
      // Restore prior work for this account_string if present, else start empty.
      const prior = initialNew?.[acc.account_string];
      initialNewMap[acc.account_string] = prior
        ? { client_id: prior.client_id, user_assignments: [...prior.user_assignments] }
        : { client_id: null, user_assignments: [] };
    });
    setNewAssignments(initialNewMap);

    const initialExistingMap: ExistingAssignmentMap = {};
    matchedAccountsNeedingEmployees.forEach((acc) => {
      const prior = initialExisting?.[acc.account_id];
      initialExistingMap[acc.account_id] = prior
        ? { user_assignments: [...prior.user_assignments] }
        : { user_assignments: [] };
    });
    setExistingAssignments(initialExistingMap);
  };

  // ---- NEW assignments ----
  const setClient = (accountString: string, clientId: number | null) =>
    setNewAssignments((prev) => ({
      ...prev,
      [accountString]: { ...prev[accountString], client_id: clientId },
    }));

  const addNewUser = (accountString: string) =>
    setNewAssignments((prev) => ({
      ...prev,
      [accountString]: {
        ...prev[accountString],
        user_assignments: [
          ...prev[accountString].user_assignments,
          { user_id: 0, commission_percentage: 20 },
        ],
      },
    }));

  const updateNewUser = (
    accountString: string,
    index: number,
    field: 'user_id' | 'commission_percentage',
    value: number
  ) =>
    setNewAssignments((prev) => {
      const updated = { ...prev[accountString] };
      updated.user_assignments = [...updated.user_assignments];
      updated.user_assignments[index] = { ...updated.user_assignments[index], [field]: value };
      return { ...prev, [accountString]: updated };
    });

  const removeNewUser = (accountString: string, index: number) =>
    setNewAssignments((prev) => {
      const updated = { ...prev[accountString] };
      updated.user_assignments = updated.user_assignments.filter((_, i) => i !== index);
      return { ...prev, [accountString]: updated };
    });

  // ---- EXISTING assignments ----
  const addExistingUser = (accountId: number) =>
    setExistingAssignments((prev) => ({
      ...prev,
      [accountId]: {
        user_assignments: [
          ...(prev[accountId]?.user_assignments || []),
          { user_id: 0, commission_percentage: 20 },
        ],
      },
    }));

  const updateExistingUser = (
    accountId: number,
    index: number,
    field: 'user_id' | 'commission_percentage',
    value: number
  ) =>
    setExistingAssignments((prev) => {
      const list = [...(prev[accountId]?.user_assignments || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [accountId]: { user_assignments: list } };
    });

  const removeExistingUser = (accountId: number, index: number) =>
    setExistingAssignments((prev) => {
      const list = (prev[accountId]?.user_assignments || []).filter((_, i) => i !== index);
      return { ...prev, [accountId]: { user_assignments: list } };
    });

  /**
   * Produce the payloads the parent's onProceed expects: NEW accounts get
   * client_id + valid employees, EXISTING accounts get only valid employees.
   * Both filters drop user_id <= 0 and commission_percentage <= 0 because the
   * backend rejects those (AccountController.php:248).
   */
  const buildPayloads = (): {
    filteredNew: Record<string, AccountAssignment>;
    filteredExisting: ExistingAccountAssignment[];
  } => {
    const filteredNew: Record<string, AccountAssignment> = {};
    Object.entries(newAssignments).forEach(([key, val]) => {
      const validUsers = val.user_assignments.filter(
        (ua) => ua.user_id > 0 && ua.commission_percentage > 0
      );
      const hasClient = !!val.client_id;
      const hasEmployees = validUsers.length > 0;
      if (hasClient || hasEmployees) {
        filteredNew[key] = { client_id: val.client_id, user_assignments: validUsers };
      }
    });

    const filteredExisting: ExistingAccountAssignment[] = Object.entries(existingAssignments)
      .map(([accountId, val]) => ({
        account_id: parseInt(accountId, 10),
        user_assignments: val.user_assignments.filter(
          (ua) => ua.user_id > 0 && ua.commission_percentage > 0
        ),
      }))
      .filter((a) => a.user_assignments.length > 0);

    return { filteredNew, filteredExisting };
  };

  return {
    newAssignments,
    existingAssignments,
    reset,
    setClient,
    addNewUser,
    updateNewUser,
    removeNewUser,
    addExistingUser,
    updateExistingUser,
    removeExistingUser,
    buildPayloads,
  };
}
